import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Paper, Stack, TextField, Typography, List, ListItem, ListItemText, IconButton, Tooltip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Modal from "./Modal";
import { createValidator, compose, required, maxLength, isDirty as dirty } from "../../utils/validationUtils";

const EditAnswerTypeModal = ({ open = false, onClose, initialData, onSubmit, onManageOptions, onQuickEditOption, loading = false }) => {
  const [form, setForm] = useState({ name: "", external_id: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: initialData?.name ?? "",
      external_id: initialData?.external_id ?? "",
    });
    setErrors({});
    setTouched({});
  }, [open, initialData]);

  const isDirty = useMemo(() => {
    const init = {
      name: initialData?.name ?? "",
      external_id: initialData?.external_id ?? "",
    };
    return dirty(init, form);
  }, [form, initialData]);

  const handleQuickEdit = (opt) => {
    onQuickEditOption?.(opt, {
      id: initialData?.id,
      name: initialData?.name,
      external_id: initialData?.external_id,
    });
  };

  const isOpenType = useMemo(() => {
    const n = (form.name || "").trim().toLowerCase();
    const e = (form.external_id || "").trim().toLowerCase();
    const openName = /^(abierta|texto|text|libre|open)$/i.test(n);
    const openExt = /^(abierta|text(_free)?|open)$/i.test(e);
    return openName || openExt;
  }, [form.name, form.external_id]);

  const validator = useMemo(
    () =>
      createValidator({
        name: compose(required("El nombre es obligatorio."), maxLength(120)),
        external_id: compose(required("El ID externo es obligatorio."), maxLength(120)),
      }),
    [],
  );

  const validate = (state = form) => validator(state);

  const handleChange = (field) => (ev) => {
    const value = ev.target.value;
    setForm((s) => ({ ...s, [field]: value }));
    if (touched[field]) setErrors(validate({ ...form, [field]: value }));
  };

  const handleBlur = (field) => () => {
    setTouched((t) => ({ ...t, [field]: true }));
    setErrors(validate());
  };

  const handleSubmit = async (ev) => {
    ev?.preventDefault();
    const v = validate();
    setErrors(v);
    setTouched({ name: true, external_id: true });
    if (Object.keys(v).length) return;

    try {
      setSubmitting(true);
      await onSubmit?.({
        id: initialData?.id,
        name: form.name.trim(),
        external_id: form.external_id.trim(),
      });
      onClose?.();
    } catch (err) {
      const msg = err?.message?.toString() || "";
      const fieldErrors = {};

      if (/duplicado/i.test(msg)) {
        fieldErrors.external_id = "Ya existe un tipo con este ID externo.";
      }

      if (Object.keys(fieldErrors).length) {
        setErrors((e) => ({ ...e, ...fieldErrors }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isSaving = submitting || loading;
  const options = Array.isArray(initialData?.options) ? initialData.options : [];

  return (
    <Modal open={open} handleClose={onClose} width="640px">
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <form onSubmit={handleSubmit} noValidate>
          <Stack gap={3}>
            {/* Encabezado */}
            <Box sx={{ p: 3, pb: 0 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Actualizar tipo de respuesta
              </Typography>
              {!!initialData?.id && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(0,0,0,0.7)",
                    backgroundColor: "rgba(0,0,0,0.08)",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    display: "inline-block",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                  }}
                >
                  {`ID: ${initialData.id}`}
                </Typography>
              )}
            </Box>

            <Box sx={{ px: 3 }}>
              {isOpenType && (
                <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
                  Este es un <b>tipo abierto</b>; no posee opciones de respuesta.
                </Typography>
              )}
            </Box>

            {/* Contenido */}
            <Box sx={{ p: 3, pt: 0 }}>
              <Stack spacing={3}>
                <TextField
                  label="Nombre"
                  value={form.name}
                  onChange={handleChange("name")}
                  onBlur={handleBlur("name")}
                  error={!!errors.name}
                  helperText={errors.name || "Digite el nombre del tipo de respuesta."}
                  inputProps={{ maxLength: 120 }}
                  required
                  fullWidth
                  autoFocus
                />

                <TextField
                  label="ID externo"
                  value={form.external_id}
                  onChange={handleChange("external_id")}
                  onBlur={handleBlur("external_id")}
                  error={!!errors.external_id}
                  helperText={errors.external_id || "Debe ser único."}
                  inputProps={{ maxLength: 120 }}
                  required
                  fullWidth
                />

                {/* Opciones */}
                <Stack gap={1}>
                  <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                    Opciones del tipo
                  </Typography>

                  {!options.length ? (
                    <Typography variant="body2" color="text.secondary">
                      [Sin opciones]
                    </Typography>
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{
                        maxHeight: 150,
                        overflow: "auto",
                        borderRadius: 1.5,
                      }}
                    >
                      <List dense disablePadding>
                        {options
                          .slice()
                          .sort((a, b) => {
                            const pa = a.position ?? Number.POSITIVE_INFINITY;
                            const pb = b.position ?? Number.POSITIVE_INFINITY;
                            if (pa !== pb) return pa - pb;
                            return (a.option || "").localeCompare(b.option || "");
                          })
                          .map((opt) => (
                            <ListItem
                              key={opt.id}
                              secondaryAction={
                                <Tooltip title="Editar opción">
                                  <span>
                                    <IconButton
                                      edge="end"
                                      size="small"
                                      aria-label={`Editar opción ${opt.label ?? opt.option ?? ""}`}
                                      onClick={() => handleQuickEdit(opt)}
                                      disabled={isOpenType}
                                    >
                                      <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              }
                              sx={{
                                "&:not(:last-of-type)": { borderBottom: "1px solid", borderColor: "divider" },
                                py: 1,
                                px: 2,
                              }}
                            >
                              <ListItemText
                                primaryTypographyProps={{ variant: "body2" }}
                                secondaryTypographyProps={{ variant: "caption", color: "text.secondary" }}
                                primary={`${opt.position ?? "?"}. ${opt.label ?? ""}`}
                                secondary={`(${opt.option ?? ""})`}
                              />
                            </ListItem>
                          ))}
                      </List>
                    </Paper>
                  )}

                  {!!onManageOptions && (
                    <Box sx={{ mt: 2 }}>
                      <Button variant="contained" onClick={onManageOptions} disabled={isOpenType}>
                        Gestionar opciones
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Stack>

              {/* Acciones */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 3, mt: 2 }}>
                <Button variant="outlined" onClick={onClose} disabled={isSaving} sx={{ borderRadius: 1, px: 3 }}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSaving || !isDirty || Object.keys(validate()).length > 0}
                  sx={{ minWidth: 120, borderRadius: 1, px: 3 }}
                >
                  {isSaving ? (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      <span>Actualizando…</span>
                    </Stack>
                  ) : (
                    "Actualizar"
                  )}
                </Button>
              </Box>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Modal>
  );
};

export default EditAnswerTypeModal;
